import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import authApi from '../../services/auth';
import {
  AuthUser,
  CaptchaResponseData,
  LoginPayload,
  LoginResponseData,
} from '../../types';
import {
  clearStoredAuth,
  getStoredAuth,
  setStoredAuth,
} from '../../utils/auth';
import { hashSha256 } from '../../utils/hash';

interface AuthState {
  token: string | null;
  user: AuthUser | null;
  captchaId: string | null;
  captchaImage: string | null;
  loading: boolean;
  captchaLoading: boolean;
  error: string | null;
}

const storedAuth = typeof window !== 'undefined' ? getStoredAuth() : { token: null, user: null };

const initialState: AuthState = {
  token: storedAuth.token,
  user: storedAuth.user,
  captchaId: null,
  captchaImage: null,
  loading: false,
  captchaLoading: false,
  error: null,
};

interface LoginSuccessPayload {
  token: string;
  user: AuthUser;
}

export const fetchCaptcha = createAsyncThunk<CaptchaResponseData, void, { rejectValue: string }>(
  'auth/fetchCaptcha',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authApi.getCaptcha();
      if (response.code !== 200) {
        return rejectWithValue(response.message || response.msg || '获取验证码失败');
      }
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || '获取验证码失败');
    }
  }
);

export const login = createAsyncThunk<LoginSuccessPayload, LoginPayload, { rejectValue: string }>(
  'auth/login',
  async (payload, { rejectWithValue }) => {
    try {
      const hashedPayload: LoginPayload = {
        ...payload,
        password: hashSha256(payload.password),
      };
      const response = await authApi.login(hashedPayload);
      if (response.code !== 200) {
        return rejectWithValue(response.message || response.msg || '登录失败');
      }
      const data = response.data as LoginResponseData;
      const user: AuthUser = {
        userId: data.userid,
        username: data.username,
      };
      setStoredAuth(data.token, user);
      return { token: data.token, user };
    } catch (error: any) {
      return rejectWithValue(error.message || '登录失败');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      state.token = null;
      state.user = null;
      clearStoredAuth({ emitEvent: false });
    },
    clearAuthError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCaptcha.pending, (state) => {
        state.captchaLoading = true;
        state.error = null;
      })
      .addCase(fetchCaptcha.fulfilled, (state, action: PayloadAction<CaptchaResponseData>) => {
        state.captchaLoading = false;
        state.captchaId = action.payload.captchaId;
        state.captchaImage = action.payload.captchaImage;
      })
      .addCase(fetchCaptcha.rejected, (state, action) => {
        state.captchaLoading = false;
        state.error = action.payload || '验证码获取失败';
      })
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action: PayloadAction<LoginSuccessPayload>) => {
        state.loading = false;
        state.token = action.payload.token;
        state.user = action.payload.user;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || '登录失败';
      });
  },
});

export const { logout, clearAuthError } = authSlice.actions;
export default authSlice.reducer;
